import { parseJson } from '@/lib/common';
import { formatDate } from '@/lib/dateTime';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

export const UNAUTH_SEARCH_COUNT_KEY = 'unauthSearchCount';
export const MAX_UNAUTH_SEARCHES = 25;

/** GraphQL operations that count toward the anonymous search limit (matches GraphQL API middleware). */
export const ANONYMOUS_RATE_LIMITED_OPERATIONS = new Set([
  'GetAnimalEbvs',
  'GetMembers',
]);

type SearchCountByDay = Record<string, number>;

function getTodayKey(): string {
  return formatDate(new Date());
}

function readCountForToday(): number {
  if (typeof window === 'undefined') return 0;
  const stored = parseJson(
    localStorage.getItem(UNAUTH_SEARCH_COUNT_KEY),
    {},
  ) as SearchCountByDay;
  return stored[getTodayKey()] ?? 0;
}

function writeCountForToday(count: number): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(
    UNAUTH_SEARCH_COUNT_KEY,
    JSON.stringify({ [getTodayKey()]: count }),
  );
}

export function getAnonymousSearchLimitResetAt(): string {
  const endOfDay = new Date();
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay.toISOString();
}

export type AnonymousSearchLimitState = {
  count: number;
  isLimited: boolean;
  isAuthenticated: boolean;
  syncFromStorage: () => void;
  setAuthenticated: (authenticated: boolean) => void;
  /** Returns false when the anonymous daily limit is already reached. */
  tryRecordSearch: () => boolean;
  clearCount: () => void;
};

const useAnonymousSearchLimitStore = create<AnonymousSearchLimitState>()(
  devtools(
    immer((set, get) => ({
      count: 0,
      isLimited: false,
      isAuthenticated: false,

      syncFromStorage: () => {
        const count = readCountForToday();
        set(() => ({
          count,
          isLimited: !get().isAuthenticated && count >= MAX_UNAUTH_SEARCHES,
        }));
      },

      setAuthenticated: (authenticated) => {
        set(() => ({ isAuthenticated: authenticated }));
        if (authenticated) {
          writeCountForToday(0);
          set(() => ({ count: 0, isLimited: false }));
          return;
        }
        get().syncFromStorage();
      },

      tryRecordSearch: () => {
        if (get().isAuthenticated) return true;

        const count = readCountForToday();
        if (count >= MAX_UNAUTH_SEARCHES) {
          set(() => ({ count, isLimited: true }));
          return false;
        }

        const next = count + 1;
        writeCountForToday(next);
        set(() => ({
          count: next,
          isLimited: next >= MAX_UNAUTH_SEARCHES,
        }));
        return true;
      },

      clearCount: () => {
        writeCountForToday(0);
        set(() => ({ count: 0, isLimited: false }));
      },
    })),
    { name: 'AnonymousSearchLimitStore' },
  ),
);

export default useAnonymousSearchLimitStore;
